import { useMemo } from 'react';

import { PencilIcon, ShareIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@radix-ui/react-icons';
import { upperFirst } from 'lodash-es';

import { MultiLoadIcon } from 'src/common/primitives/icon/MultiLoadIcon';

import { getTripCalendarTheme } from '../getTripCalendarTheme';
import { EventBadge } from './EventBadge';
import { EventBadgesProps } from './EventBadgesProps';
import { getSpaceTypeBadgeLabel } from './getSpaceTypeBadgeLabel';

export function useEventBadges({ trip, type }: EventBadgesProps) {
  const theme = getTripCalendarTheme(trip, type);

  const badges = useMemo(() => {
    // Push things in order of importance
    const newBadges = [];
    if (trip.correlation_id) {
      newBadges.push(
        <EventBadge
          trip={trip}
          type={type}
          tooltipContent="Multi-leg trip"
          dataTestId="multi-leg-trip-icon"
        >
          <ShareIcon className="h-3 w-3" />
        </EventBadge>
      );
    }

    if ('multiload_id' in trip && trip.multiload_id) {
      newBadges.push(
        <EventBadge
          trip={trip}
          type={type}
          tooltipContent="Multi-loaded trip"
          dataTestId="multi-load-trip-icon"
        >
          <MultiLoadIcon color={theme.text} />
        </EventBadge>
      );
    }

    if (trip.is_locked) {
      newBadges.push(
        <EventBadge
          trip={trip}
          type={type}
          tooltipContent="Locked trip"
          dataTestId="locked-trip-icon"
        >
          <LockClosedIcon className="h-3 w-3" />
        </EventBadge>
      );
    }

    newBadges.push(
      <EventBadge
        styleOverrides={{ paddingLeft: '4px', paddingRight: '4px' }}
        trip={trip}
        type={type}
        tooltipContent={upperFirst(trip.space_type)}
        dataTestId="space-type-icon"
      >
        {getSpaceTypeBadgeLabel(trip.space_type)}
      </EventBadge>
    );

    if (
      'missing_any_required_signature' in trip &&
      trip.missing_any_required_signature
    ) {
      newBadges.push(
        <EventBadge
          styleOverrides={{
            backgroundColor: 'white',
            color: 'red',
            borderColor: 'red',
            borderWidth: '1px',
          }}
          trip={trip}
          type={type}
          tooltipContent="Driver missed collecting signature and needs to be alerted"
          dataTestId="event-missed-signature-badge"
        >
          <PencilIcon
            className="h-3 w-3"
            data-testid={`event-missed-signature-icon-${trip.id}`}
          />
        </EventBadge>
      );
    }

    return newBadges;
  }, [trip, type, theme]);

  return badges;
}
